/**
 * ConnectionStorage - Manages local storage of family connections and provider relationships
 * 
 * Storage Structure:
 * - Key: connections_{familyId}
 * - Value: FamilyConnections containing array of ConnectionDto
 * 
 * Features:
 * - Multiple connections per family
 * - Search and filter capabilities
 * - Automatic timestamp tracking
 * - Connection deduplication by connectionId
 * - Support for healthcare providers, family members, and community resources
 */

import type { StorageInterface } from '../stores/childNavStores';
import type { AccountConnectionDto } from '../types/api/service';
import type { 
  ConnectionDto, 
  FamilyConnections, 
  ConnectionSearchCriteria, 
  ConnectionStats,
  ConnectionType 
} from '../types/ConnectionDto';

export class ConnectionStorage {
  private storage: StorageInterface;
  private keyPrefix: string = 'connections_';

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  /**
   * Save a connection to storage for a specific family
   */
  async saveConnection(familyId: string, connection: ConnectionDto): Promise<boolean> {
    try {
      console.log(`💾 ConnectionStorage: Saving connection for family ${familyId}`);
      console.log(`   Connection: ${connection.name} (${connection.connectionId})`);

      // Get existing connections for this family
      const familyConnections = await this.getFamilyConnections(familyId);
      const updatedConnections = [...familyConnections.connections];

      // Find existing connection by connectionId
      const existingIndex = updatedConnections.findIndex(c => c.connectionId === connection.connectionId);

      if (existingIndex >= 0) {
        // Update existing connection
        updatedConnections[existingIndex] = { ...connection, modifiedDate: new Date().toISOString() };
        console.log(`   🔄 Updated existing connection: ${connection.name}`);
      } else {
        // Add new connection
        updatedConnections.push({ ...connection, modifiedDate: new Date().toISOString() });
        console.log(`   ➕ Added new connection: ${connection.name}`);
      }

      const updatedFamilyConnections: FamilyConnections = {
        familyId: familyId,
        connections: updatedConnections,
        lastUpdated: new Date().toISOString()
      };

      // Save to storage
      const key = this.keyPrefix + familyId;
      await this.storage.setItem(key, JSON.stringify(updatedFamilyConnections));
      const saved = true; // setItem returns void, so assume success if no error thrown

      console.log(`💾 ConnectionStorage: Save result: ${saved ? 'SUCCESS' : 'FAILED'}`);
      console.log(`   Total connections for family ${familyId}: ${updatedConnections.length}`);

      return saved;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Failed to save connection:`, error);
      return false;
    }
  }

  /**
   * Get all connections for a specific family
   */
  async getFamilyConnections(familyId: string): Promise<FamilyConnections> {
    try {
      const key = this.keyPrefix + familyId;
      const stored = await this.storage.getItem(key);

      if (stored) {
        const parsed = JSON.parse(stored) as FamilyConnections;
        console.log(`📂 ConnectionStorage: Retrieved ${parsed.connections.length} connections for family ${familyId}`);
        return parsed;
      }

      // Return empty structure if no data found
      const emptyStructure: FamilyConnections = {
        familyId: familyId,
        connections: [],
        lastUpdated: new Date().toISOString()
      };

      console.log(`📂 ConnectionStorage: No connections found for family ${familyId} - returning empty structure`);
      return emptyStructure;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Failed to get connections:`, error);
      return {
        familyId: familyId,
        connections: [],
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Get a specific connection by ID
   */
  async getConnection(familyId: string, connectionId: string): Promise<ConnectionDto | null> {
    try {
      const familyConnections = await this.getFamilyConnections(familyId);
      const connection = familyConnections.connections.find(c => c.connectionId === connectionId);

      if (connection) {
        console.log(`🔍 ConnectionStorage: Found connection ${connection.name} for family ${familyId}`);
        return connection;
      }

      console.log(`🔍 ConnectionStorage: Connection ${connectionId} not found for family ${familyId}`);
      return null;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Failed to get connection:`, error);
      return null;
    }
  }

  /**
   * Search connections with filtering criteria
   */
  async searchConnections(familyId: string, criteria: ConnectionSearchCriteria = {}): Promise<ConnectionDto[]> {
    try {
      console.log(`🔍 ConnectionStorage: Searching connections for family ${familyId}`, criteria);

      const familyConnections = await this.getFamilyConnections(familyId);
      let filteredResults = [...familyConnections.connections];

      // Apply filters
      if (criteria.connectionType) {
        filteredResults = filteredResults.filter(c => c.connectionType === criteria.connectionType);
      }

      if (criteria.status) {
        filteredResults = filteredResults.filter(c => c.status === criteria.status);
      }

      if (criteria.isActive !== undefined) {
        filteredResults = filteredResults.filter(c => c.isActive === criteria.isActive);
      }

      if (criteria.hasPhone !== undefined) {
        filteredResults = filteredResults.filter((connection: ConnectionDto) => 
          criteria.hasPhone ? !!connection.phone : !connection.phone
        );
      }

      if (criteria.hasEmail !== undefined) {
        filteredResults = filteredResults.filter((connection: ConnectionDto) => 
          criteria.hasEmail ? !!connection.email : !connection.email
        );
      }

      if (criteria.nameContains) {
        const nameFilter = criteria.nameContains.toLowerCase();
        filteredResults = filteredResults.filter(c => 
          c.name.toLowerCase().includes(nameFilter)
        );
      }

      if (criteria.organizationContains) {
        const orgFilter = criteria.organizationContains.toLowerCase();
        filteredResults = filteredResults.filter(c => 
          c.organization?.toLowerCase().includes(orgFilter)
        );
      }

      if (criteria.specialty) {
        filteredResults = filteredResults.filter(c => c.specialty === criteria.specialty);
      }

      console.log(`🔍 ConnectionStorage: Search returned ${filteredResults.length} connections`);
      return filteredResults;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Search failed:`, error);
      return [];
    }
  }

  /**
   * Delete a connection by ID
   */
  async deleteConnection(familyId: string, connectionId: string): Promise<boolean> {
    try {
      console.log(`🗑️ ConnectionStorage: Deleting connection ${connectionId} for family ${familyId}`);

      const familyConnections = await this.getFamilyConnections(familyId);
      const updatedConnections = familyConnections.connections.filter(c => c.connectionId !== connectionId);

      if (updatedConnections.length === familyConnections.connections.length) {
        console.log(`⚠️ ConnectionStorage: Connection ${connectionId} not found - nothing to delete`);
        return false;
      }

      const updatedFamilyConnections: FamilyConnections = {
        familyId: familyId,
        connections: updatedConnections,
        lastUpdated: new Date().toISOString()
      };

      const key = this.keyPrefix + familyId;
      await this.storage.setItem(key, JSON.stringify(updatedFamilyConnections));
      const saved = true; // setItem returns void, so assume success if no error thrown

      console.log(`🗑️ ConnectionStorage: Delete result: ${saved ? 'SUCCESS' : 'FAILED'}`);
      return saved;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Failed to delete connection:`, error);
      return false;
    }
  }

  /**
   * Get connection statistics
   */
  async getConnectionStats(familyId: string): Promise<ConnectionStats> {
    try {
      const familyConnections = await this.getFamilyConnections(familyId);
      
      const stats: ConnectionStats = {
        total: familyConnections.connections.length,
        active: familyConnections.connections.filter(c => c.isActive).length,
        inactive: familyConnections.connections.filter(c => !c.isActive).length,
        byType: {} as Record<ConnectionType, number>,
        lastSyncDate: familyConnections.lastUpdated
      };

      // Count by connection type
      const connectionTypes: ConnectionType[] = [
        'healthcare_provider', 'therapist', 'specialist', 'family_member', 
        'caregiver', 'emergency_contact', 'community_resource', 'other'
      ];
      
      connectionTypes.forEach(type => {
        stats.byType[type] = familyConnections.connections.filter(c => c.connectionType === type).length;
      });

      console.log(`📊 ConnectionStorage: Stats for family ${familyId}:`, stats);
      return stats;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Failed to get stats:`, error);
      return {
        total: 0,
        active: 0,
        inactive: 0,
        byType: {} as Record<ConnectionType, number>,
        lastSyncDate: new Date().toISOString()
      };
    }
  }

  /**
   * Clear all connections for a family
   */
  async clearFamilyConnections(familyId: string): Promise<boolean> {
    try {
      console.log(`🧹 ConnectionStorage: Clearing all connections for family ${familyId}`);
      
      const key = this.keyPrefix + familyId;
      await this.storage.removeItem(key);
      const removed = true; // removeItem returns void, so assume success if no error thrown
      
      console.log(`🧹 ConnectionStorage: Clear result: ${removed ? 'SUCCESS' : 'FAILED'}`);
      return removed;
    } catch (error) {
      console.error(`❌ ConnectionStorage: Failed to clear connections:`, error);
      return false;
    }
  }

  /**
   * Convert backend AccountConnectionDto to local ConnectionDto
   */
  static convertFromBackendItem(
    backendItem: AccountConnectionDto,
    familyId: string,
    additionalData: Partial<ConnectionDto> = {}
  ): ConnectionDto {
    const now = new Date().toISOString();
    
    // Determine connection type based on available data
    const inferConnectionType = (): ConnectionType => {
      if (additionalData.connectionType) return additionalData.connectionType;
      
      // Simple heuristics - could be enhanced based on more backend data
      const name = backendItem.DName.toLowerCase();
      const permissions = backendItem.Permissions.toLowerCase();
      
      if (name.includes('doctor') || name.includes('physician') || permissions.includes('medical')) {
        return 'healthcare_provider';
      }
      if (name.includes('therapist') || name.includes('therapy')) {
        return 'therapist';
      }
      if (name.includes('specialist')) {
        return 'specialist';
      }
      if (permissions.includes('family') || permissions.includes('parent')) {
        return 'family_member';
      }
      if (permissions.includes('caregiver') || permissions.includes('care')) {
        return 'caregiver';
      }
      if (permissions.includes('emergency')) {
        return 'emergency_contact';
      }
      
      return 'other';
    };

    const isLinked = backendItem.Type === '1';

    return {
      connectionId: backendItem.PatConnId,
      name: backendItem.DName,
      permissions: backendItem.Permissions,
      status: backendItem.Status,
      familyId: familyId,
      connectionType: inferConnectionType(),
      isActive: backendItem.Status.toLowerCase().includes('active') || backendItem.Status === '1',
      createdDate: additionalData.createdDate || now,
      modifiedDate: additionalData.modifiedDate || now,
      syncDate: now,
      email: isLinked ? undefined : (backendItem.Link || additionalData.email),
      phone: additionalData.phone,
      specialty: additionalData.specialty,
      organization: additionalData.organization,
      notes: additionalData.notes,
      lastContact: additionalData.lastContact
    };
  }

  /**
   * Helper method to get all storage keys for connections
   * Note: StorageInterface doesn't have getAllKeys, so we'll simulate it
   */
  private async getAllFamilyKeys(): Promise<string[]> {
    try {
      // Since StorageInterface doesn't have getAllKeys, we'll maintain a separate index
      // For now, return empty array and log the limitation
      console.warn('⚠️ ConnectionStorage: getAllKeys not available in StorageInterface - returning empty array');
      return [];
    } catch (error) {
      console.error('❌ ConnectionStorage: Error getting all keys:', error);
      return [];
    }
  }
}